import { gql } from '@apollo/client';
import client from 'apollo/client';
import { userLoginFields } from 'common/constants/query-fields/user';

/**
 * Generate reset token method
 *
 * @param  {string} email
 * @return {Promise}
 */
const generateResetToken = (email) =>
  client.mutate({
    mutation: gql`
      mutation generatePasswordResetToken($input: ForgotPasswordInput!) {
        generatePasswordResetToken(input: $input) {
          created
          message
        }
      }
    `,
    variables: { input: { email } }
  });

/**
 * Login method
 *
 * @param  {string} username
 * @param  {Password} password
 * @return {Promise}
 */
const login = ({ username, password }) =>
  client.mutate({
    mutation: gql`
      mutation login($input: LoginInput!) {
        login(input: $input) {
          access_token
          refresh_token
          expires_in
          token_type
          user {
            ${userLoginFields}
          }
        }
      }
    `,
    variables: { input: { username, password } }
  });

/**
 * Logout method
 *
 * @return {Promise}
 */
const logout = () =>
  client.mutate({
    mutation: gql`
      mutation logout {
        logout {
          status
          message
        }
      }
    `
  });

/**
 * Refresh token method
 *
 * @param  {string} token
 * @return {Promise}
 */
const refreshToken = (token) =>
  client.mutate({
    mutation: gql`
      mutation refreshToken($input: RefreshTokenInput) {
        refreshToken(input: $input) {
          access_token
          refresh_token
          expires_in
          token_type
        }
      }
    `,
    variables: { input: { refresh_token: token } }
  });

/**
 * Reset password method
 *
 * @param  {Object} input
 * @return {Promise}
 */
const resetPassword = (input) =>
  client.mutate({
    mutation: gql`
      mutation updatePasswordWithToken($input: NewPasswordWithCodeInput!) {
        updatePasswordWithToken(input: $input) {
          updated
          message
        }
      }
    `,
    variables: { input }
  });

/**
 * Send reset token method
 *
 * @param  {string} email
 * @return {Promise}
 */
const sendResetToken = (email) =>
  client.mutate({
    mutation: gql`
      mutation sendPasswordResetToken($input: ForgotPasswordInput!) {
        sendPasswordResetToken(input: $input) {
          sent
          message
        }
      }
    `,
    variables: { input: { email } }
  });

/**
 * Self-registration method for new clients
 *
 * @param  {Object} input
 * @return {Promise}
 */
const selfRegister = (input) => {
  const formatted = {
    ...input,
    birthdate: input.birthdate
      ? new Date(input.birthdate).toISOString().split('T')[0]
      : null
  };
  return client.mutate({
    mutation: gql`
      mutation selfRegister($input: SelfRegisterInput!) {
        selfRegister(input: $input) {
          status
          tokens {
            access_token
            refresh_token
            expires_in
            token_type
            user {
              ${userLoginFields}
            }
          }
        }
      }
    `,
    variables: { input: formatted }
  });
};

const verifyEmail = (token, email) =>
  client.mutate({
    mutation: gql`
      mutation verifyEmail($input: VerifyEmailInput!) {
        verifyEmail(input: $input) {
          access_token
          token_type
          user {
            ${userLoginFields}
          }
        }
      }
    `,
    variables: { input: { token, email } }
  });

const resendVerificationEmail = (email) =>
  client.mutate({
    mutation: gql`
      mutation resendVerificationEmail($email: String!) {
        resendVerificationEmail(email: $email) {
          sent
          message
        }
      }
    `,
    variables: { email }
  });

const checkPasswordResetToken = (email, token) =>
  client.mutate({
    mutation: gql`
      mutation checkPasswordResetToken($input: CheckPasswordResetTokenInput!) {
        checkPasswordResetToken(input: $input) {
          valid
          message
        }
      }
    `,
    variables: { input: { email, token } }
  });

export default {
  checkPasswordResetToken,
  generateResetToken,
  login,
  logout,
  refreshToken,
  resendVerificationEmail,
  resetPassword,
  selfRegister,
  sendResetToken,
  verifyEmail
};
